"use client";

import styles from "@/styles/contact/contact-form.module.scss"
import { useEffect, useState, useRef, useCallback } from "react";
import { Button, Form, Input, Skeleton } from 'react-vant'
import { toast } from "@/components/global/Toast";
import { errorUtils } from "@/utils/error";
import contactApi from '@/service/modules/contactApi'
import systemApi from '@/service/modules/systemApi'
import myAccountApi from '@/service/modules/myAccountApi'
import ReCAPTCHA from 'react-google-recaptcha';
import ImageUploader from "@/components/global/ImageUploader";
import type { ContactSubject, ContactFormValues } from "@/types/contact";
import { UploaderValueItem } from "react-vant";
import Dialog from "@/components/global/Dialog";
import Image from "next/image";

export default function ContactForm() {
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const [form] = Form.useForm()

  const [subjects, setSubjects] = useState<ContactSubject[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedSub, setSelectedSub] = useState<string | null>(null);
  const [subjectNotes, setSubjectNotes] = useState<Record<string, string>>({});
  const [subjectTypeMessageNotes, setSubjectTypeMessageNotes] = useState<Record<string, string>>({});
  const [currentNotes, setCurrentNotes] = useState('')
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [fileList, setFileList] = useState<UploaderValueItem[]>([]);
  const [showPidGuidePopup, setShowPidGuidePopup] = useState(false);

  interface DataSetting {
    [key: string]: string[]; // key 是字符串，值是字符串数组
  }
  const [dataSetting, setDataSetting] = useState<DataSetting>({});

  const [siteKey, setSiteKey] = useState('')
  const getRecaptchaSiteKey = async () => {
    try {
      const res = await systemApi.getRecaptchaSiteKey()
      if (res.data && res.data.siteKey) {
        setSiteKey(res.data.siteKey)
      }
    } catch (error) {
      console.error(error)
    }
  }

  // 模拟获取接口数据
  const fetchSubjects = async () => {
    setLoading(true);
    try {
      const response = await contactApi.getContactUsSetting()

      if (response && response.data.subjects) {
        setSubjects(response.data.subjects);
        setSubjectNotes(response.data.subjectNotes);
        setSubjectTypeMessageNotes(response.data.subjectTypeMessageNotes);
        setDataSetting(response.data.dataSetting);
      }
    } catch (error) {
      console.error("Failed to fetch subjects:", error);
    } finally {
      setLoading(false);
    }
  };

  // 初始化选项的默认值
  useEffect(() => {
    if (subjects.length > 0) {
      const initialSubject = subjects[0].title;
      const initialSub = Object.keys(subjects[0].sub)[0] || null;
      setSelectedSubject(initialSubject);
      setSelectedSub(initialSub);
      form.setFieldsValue({
        mainSubject: initialSubject,
        subject: initialSub
      });
    }
  }, [subjects, form]);


  // 获取用户信息，填入表单
  const getCustomerInfo = useCallback(async () => {
    try {
      const res = await myAccountApi.getCustomerInfo()
      if (res.data) {
        form.setFieldsValue({
          customerName: res.data?.customer.firstName || '',
          customerEmail: res.data?.customer.email || '',
          confirmEmail: res.data?.customer.email || ''
        });
      }
    } catch (error) {
      console.error(error)
    }
  }, [form])

  // 判断是否展示字段
  const shouldDisplayField = (field: string) => {
    return dataSetting[field]?.includes(selectedSub ?? ""); // 如果 selectedSub 是 null，用空字符串代替
  };

  useEffect(() => {
    getRecaptchaSiteKey()
    fetchSubjects();

    // 如果有登录，则获取用户信息
    if (localStorage.getItem('userEmail')) {
      getCustomerInfo()
    }
  }, [getCustomerInfo]);

  // 主问题类型改变时
  const handleSubjectChange = (value: string) => {
    // 获取对应的子问题选项
    const currentSubject = subjects.find(subject => subject.title === value);
    const subOptions = currentSubject?.sub ? Object.keys(currentSubject.sub) : [];
    
    // 更新状态
    setSelectedSubject(value);
    
    // 自动选择第一个子选项
    const firstSubKey = subOptions[0] || null;
    setSelectedSub(firstSubKey);
    
    // 同步更新表单字段值
    form.setFieldsValue({
      subject: firstSubKey
    });
  };

  // 子问题类型改变时
  const handleSubChange = (value: string) => {
    setSelectedSub(value);
    form.setFieldsValue({ subject: value });
  };

  // 监听 selectedSub 更新 currentNotes
  useEffect(() => {
    if (selectedSub && subjectNotes[selectedSub]) {
      setCurrentNotes(subjectNotes[selectedSub]);
    } else {
      setCurrentNotes(""); // 如果没有匹配的文案，清空
    }
  }, [selectedSub, subjectNotes]);

  const handleFileListChange = (list: UploaderValueItem[]) => {
    setFileList(list);
    const urls = list
      .filter(item => item.url && (!item.status || item.status === 'done'))
      .map(item => item.url as string);
    setUploadedUrls(urls);
  };

  const onFinish = async(values: ContactFormValues) => {
    try {
      if (submitLoading) {
        return
      }

      const recaptcha = recaptchaRef.current?.getValue();
      if (!siteKey || !recaptcha) {
        toast.show('Please verify you are not a robot')
        return
      }
      
      // 定义带动态键的对象类型
      const result: Record<string, string> = {}; 
      
      const maxFields = 5;
      for (let i = 0; i < maxFields; i++) {
        result[`opPhoto${i + 1}`] = uploadedUrls[i] || ""; // 动态赋值
      }

      // 赋值subSubject
      const subSubjectList = subjects.find((subject) => subject.title === values.mainSubject)?.sub || {}
      const subSubject = subSubjectList[values.subject]

      const finalValues = { recaptcha, subSubject, ...values, ...result };

      setSubmitLoading(true)
      const res = await contactApi.submitContactUsData(finalValues)

      setSubmitLoading(false)
      if (res.data) {
        toast.show('Submit Success')

        setTimeout(() => {
          window.location.reload()
        }, 2500)
      }
    } catch (error: unknown) {
      setSubmitLoading(false)
      const message = errorUtils.getErrorMessage(error)
      toast.show(message)
    }
  }

  return (
    <div className={`${styles['contact-form-box']}`}>
      {loading && (
        <div className={styles["skeleton-box"]} style={{ position: 'relative', minHeight: '200px', marginTop: '40px' }}>
          <Skeleton />
          <Skeleton style={{ marginTop: "28px" }} />
          <Skeleton style={{ marginTop: "28px" }} />
        </div>
      )}
      {!loading && (
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          footer={
            <div className={styles["submit-button-box"]}>
              <Button
                round
                nativeType="submit"
                type="primary"
                className={styles["submit-button"]}
                style={{ fontWeight: "700" }}
                disabled={submitLoading}
                loading={submitLoading}
                loadingText="Sending..."
                block
              >
                Send Now
              </Button>
            </div>
          }
        >
          <div className={styles['enquiry-type-select']}>
            <Form.Item 
              label="Main Reason"
              name="mainSubject"
              rules={[{ required: true, message: 'Main Reason is required' }]}
            >
              <select
                style={{ width: '100%' }}
                value={selectedSubject || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  handleSubjectChange(value);
                  form.setFieldsValue({ mainSubject: value });
                }}
              >
                {subjects.map((subject) => (
                  <option key={subject.title} value={subject.title}>
                    {subject.title}
                  </option>
                ))}
              </select>
            </Form.Item>

            {/* 子问题类型选择 */}
            <Form.Item 
              label="Subject"
              name="subject"
              rules={[{ required: true, message: 'Subject is required' }]}
            >
              <select
                style={{ width: '100%' }}
                value={selectedSub || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  handleSubChange(value);
                  form.setFieldsValue({ subject: value });
                }}
                disabled={!selectedSubject}
              >
                {selectedSubject && 
                  Object.entries(
                    subjects.find(s => s.title === selectedSubject)?.sub || {}
                  ).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value as string}
                    </option>
                  ))
                }
              </select>
            </Form.Item>
          </div>

          {/* 文案展示 */}
          {currentNotes && (
            <div
              style={{
                marginBottom: "24px",
                padding: "12px",
              }}
              dangerouslySetInnerHTML={{ __html: currentNotes }}
            ></div>
          )}

          {/* 动态表单内容 */}
          {selectedSub && 
            <div>

              {/* regEmail */}
              { shouldDisplayField("regEmail") &&
                <Form.Item
                  label="Registered Email Address"
                  name="regEmail"
                  rules={[
                    { required: true, message: `Registered Email Address is required`},
                    { type: 'email', message: `Invalid Email Address`},
                  ]}
                >
                  <Input placeholder="" />
                </Form.Item>
              }

              {/* opRegEmail */}
              { shouldDisplayField("opRegEmail") &&
                <Form.Item
                  label="Registered Email Address"
                  name="opRegEmail"
                  rules={[
                    { required: false, message: `Registered Email Address is required`},
                    { type: 'email', message: `Invalid Email Address`},
                  ]}
                >
                  <Input placeholder="" />
                </Form.Item>
              }

              {/* orderID */}
              { shouldDisplayField("orderID") &&
                <Form.Item
                  label="Order ID"
                  name="orderID"
                  rules={[
                    { required: true, message: `Order ID is required`}
                  ]}
                >
                  <Input placeholder="" />
                </Form.Item>
              }

              {/* opOrderID */}
              { shouldDisplayField("opOrderID") &&
                <Form.Item
                  label="Order ID"
                  name="opOrderID"
                  rules={[
                    { required: true, message: `Order ID is required`}
                  ]}
                >
                  <Input placeholder="" />
                </Form.Item>
              }

              {/* sku */}
              { shouldDisplayField("sku") &&
                <Form.Item
                  label={
                    <span>
                      Product Code (PID) of the item you ordered: &nbsp;
                      <span
                        tabIndex={0}
                        className={styles["form-help-link"]}
                        onClick={(e) => { e.preventDefault(); setShowPidGuidePopup(true); }}
                      >
                        How to Find a PID
                      </span>
                    </span>
                  }
                  name="sku"
                >
                  <Input placeholder="" />
                </Form.Item>
              }

              {/* opSku */}
              { shouldDisplayField("opSku") &&
                <Form.Item
                  label={
                    <span>
                      Product Code (PID): &nbsp;
                      <span
                        role="button"
                        tabIndex={0}
                        className={styles["form-help-link"]}
                        onClick={(e) => { e.preventDefault(); setShowPidGuidePopup(true); }}
                      >
                        How to Find a PID
                      </span>
                    </span>
                  }
                  name="opSku"
                >
                  <Input placeholder="" />
                </Form.Item>
              }

              {/* Qty */}
              { shouldDisplayField("qty") &&
                <Form.Item
                  label="Qty"
                  name="qty"
                  rules={[
                    { required: true, message: `Qty is required`}
                  ]}
                >
                  <Input placeholder="" />
                </Form.Item>
              }

              {/* postCode */}
              { shouldDisplayField("postCode") &&
                <Form.Item
                  label={
                    <span>
                      Delivery Postcode: &nbsp;
                      <a href="http://www.nzpost.co.nz/tools/address-postcode-finder" target="_blank" className={styles["form-help-link"]}>
                        How to Find the Postcode
                      </a>
                    </span>
                  }
                  name="postCode"
                >
                  <Input placeholder="" />
                </Form.Item>
              }

              {/* opPostCode */}
              { shouldDisplayField("opPostCode") &&
                <Form.Item
                  label={
                    <span>
                      Delivery Postcode: &nbsp;
                      <a href="http://www.nzpost.co.nz/tools/address-postcode-finder" target="_blank" className={styles["form-help-link"]}>
                        How to Find the Postcode
                      </a>
                    </span>
                  }
                  name="opPostCode"
                >
                  <Input placeholder="" />
                </Form.Item>
              }

              {/* opPartNo */}
              { shouldDisplayField("opPartNo") &&
                <Form.Item
                  label={'Part Number: (Kindly refer to User manual if applicable or send us photo(s) of the part in question)'}
                  name="opPartNo"
                >
                  <Input placeholder="" />
                </Form.Item>
              }

              {/* opPhoto3 */}
              { (shouldDisplayField("opPhoto3") || shouldDisplayField("opPhoto5")) &&
                <div className="contact-upload-field">
                <Form.Item
                  label={
                    <>
                      <span>Please upload your photo. <span style={{ color: 'red' }}>(Photo size cannot be over 500kb)</span> (You can use this website to compress your photo)</span>
                      <span style={{ fontSize: '12px', display: 'block', lineHeight: '14px', paddingTop: '4px' }}><strong>Note: </strong> Please make sure you take a photo that clearly shows the issue being reported. Our Customer Service team should be able to respond to your inquiry much quicker if they can easily identify the problem, which enables us to provide a faster resolution for your issue.</span>
                    </>
                  }
                >
                  <ImageUploader
                    value={fileList}
                    maxCount={shouldDisplayField("opPhoto5") ? 5 : 3}
                    accept="image/*"
                    onChange={handleFileListChange}
                  />
                </Form.Item>
                </div>
              }

              {/* message */}
              <Form.Item
                label={
                  <>
                    <span>Your Message: &nbsp;</span>
                    <span style={{ fontSize: '12px', display: 'block', lineHeight: '14px', paddingTop: '4px' }}>(Note: Please provide detailed information regarding your issue. This is for our Customer Service team to immediately resolve your case.)</span>
                  </>
                }
                name="message"
                rules={[
                  { required: true, message: `Message is required`}
                ]}
              >
                <Input.TextArea 
                  maxLength={2000} 
                  showWordLimit 
                />
              </Form.Item>
              {subjectTypeMessageNotes[selectedSub] && (
                <div style={{ marginTop: '-12px', marginBottom: '24px', fontSize: '14px', color: '#333' }}>
                  {subjectTypeMessageNotes[selectedSub]}
                </div>
              )}

              {/* customerName */}
              <Form.Item
                label="Your Name"
                name="customerName"
                rules={[
                  { required: true, message: `Your Name is required`}
                ]}
              >
                <Input />
              </Form.Item>

              {/* customerEmail */}
              <Form.Item
                label="Your Email Address"
                name="customerEmail"
                rules={[
                  { required: true, message: `Your Email Address is required`},
                  { type: 'email', message: `Invalid Email Address`},
                ]}
              >
                <Input />
              </Form.Item>

              {/* confirmEmail */}
              <Form.Item
                label="Confirm Your Email Address"
                name="confirmEmail"
                rules={[
                  { required: true, message: `Confirm Email Address is required`},
                  { type: 'email', message: `Invalid Email Address`},
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('customerEmail') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Your Email Address are not indentical.'));
                    },
                  }),
                ]}
              >
                <Input />
              </Form.Item>
            </div>
          }
          
          {
            !!(siteKey) &&
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={siteKey}
              type={'audio'}
            />
          }
        </Form>
      )}

      <Dialog
        visible={showPidGuidePopup}
        title="HOW TO FIND A PID?"
        closeable
        showConfirmButton={false}
        showCancelButton={false}
        onClose={() => setShowPidGuidePopup(false)}
      >
        <ul className={styles["pid-guide-tips"]}>
          <li>Go to item page;</li>
          <li>PID number is located at the top of the product&apos;s title.</li>
        </ul>
        <div style={{ maxWidth: '100%', overflow: 'hidden', marginTop: '16px', borderRadius: '4px', border: '1px solid #dedede', boxSizing: 'border-box' }}>
          <Image
            src={`${process.env.IMAGE_DOMAIN}/contact/how-to-find-pid.png`}
            alt="How to Find a PID - Product page showing PID below the title"
            width={400}
            height={300}
            style={{ width: '100%', height: 'auto', padding: '4px 2px', display: 'block', borderRadius: '4px' }}
          />
        </div>
      </Dialog>
    </div>
  )
}